import { v4 as uuidv4 } from 'uuid';
import { getAppCopy, getHistoryStorageCopy, historyActionLabel, HISTORY_STORAGE_LOCALE } from '../i18n';
import { Sheet, Pouch, Group, POUCH_COLORS, GROUP_COLORS, SheetHistoryEntry, HistoryActionType } from '../types';
import { snapToGrid } from '../logic/GroupLogic';
import { createDefaultLayer } from '../logic/LayerLogic';

/** シート作成時の初期パウチ基準位置（グリッドオン時は snapToGrid で丸める） */
const INITIAL_POUCH_BASE_X = 60;
const INITIAL_POUCH_BASE_Y = 90;

/** ウェルカムシートのプリセット配置（レイヤー名行と重ならない位置） */
const WELCOME_POUCH_LAYOUT = [
  { x: 120, y: 200 },
  { x: 300, y: 200 },
  { x: 120, y: 360 },
  { x: 300, y: 360 },
] as const;

/** [0]=青グループ, [1]=黄グループ */
const WELCOME_GROUP_PATHS = [
  'M235 25L365 25Q375 25 375 35L375 436Q375 446 365 446L235 446Q225 446 225 436L225 35Q225 25 235 25Z',
  'M 31 113 L 565 113 Q 575 113 575 123 L 575 265 Q 575 275 565 275 L 31 275 Q 21 275 21 265 L 21 123 Q 21 113 31 113 Z',
] as const;

/**
 * 初期パウチを作成する
 * @returns 初期パウチオブジェクト
 */
const createDefaultPouch = (): Pouch => {
  return {
    id: uuidv4(),
    value: 0,
    multiplier: 1,
    x: snapToGrid(INITIAL_POUCH_BASE_X),
    y: snapToGrid(INITIAL_POUCH_BASE_Y),
    isNegative: false,
    zIndex: 200001, // 初期z-index
    color: POUCH_COLORS[0].id, // デフォルト色
    _animateFromZero: false // 初期表示時はアニメーションしない
  };
};

/**
 * 初回起動時専用のウェルカムパウチ群を作成する
 * @returns ウェルカムパウチ配列
 */
const createWelcomePouches = (): Pouch[] => {
  const w = getAppCopy().welcome;
  const pouchDefs = [
    { title: w.pouch1, value: 100, color: 'orange', zIndex: 200061 },
    { title: w.pouch2, value: 200, color: 'pink', zIndex: 200061 },
    { title: w.pouch3, value: 400, color: 'yellow-green', zIndex: 200058 },
    { title: w.pouch4, value: 800, color: 'orange', zIndex: 200060 },
  ] as const;

  return pouchDefs.map((def, index) => {
    const pos = WELCOME_POUCH_LAYOUT[index];
    return {
      id: uuidv4(),
      title: def.title,
      value: def.value,
      multiplier: 1,
      x: pos.x,
      y: pos.y,
      isNegative: false,
      zIndex: def.zIndex,
      color: def.color,
      _animateFromZero: false,
    };
  });
};

/**
 * 初回起動時専用のウェルカムグループ群を作成する
 * @param pouches ウェルカムパウチ配列
 * @returns ウェルカムグループ配列
 */
const createWelcomeGroups = (pouches: Pouch[]): Group[] => {
  if (pouches.length < 4) return [];
  const w = getAppCopy().welcome;

  return [
    {
      id: uuidv4(),
      path: WELCOME_GROUP_PATHS[0],
      pouches: [pouches[1].id, pouches[3].id],
      color: 'blue',
      placeholderText: w.group2Placeholder,
      title: w.group2Title,
      zIndex: 102000,
    },
    {
      id: uuidv4(),
      path: WELCOME_GROUP_PATHS[1],
      pouches: [pouches[0].id, pouches[1].id],
      color: 'yellow',
      placeholderText: w.group1Placeholder,
      title: w.group1Title,
      zIndex: 101130,
    },
  ];
};

/**
 * 初期履歴エントリを作成する
 * @param pouches パウチ配列
 * @param groups グループ配列
 * @returns 初期履歴エントリ
 */
const createInitialHistoryEntry = (pouches: Pouch[], groups: Group[]): SheetHistoryEntry => {
  const layers = [createDefaultLayer()];
  return {
    timestamp: new Date(),
    groups: groups,
    pouches: pouches,
    description: historyActionLabel(HistoryActionType.INIT, HISTORY_STORAGE_LOCALE),
    type: HistoryActionType.INIT,
    targetId: undefined,
    layers: layers.map(l => ({ ...l })),
    activeLayerId: layers[0].id,
  };
};

/**
 * Bridge API 用の空シート（初期パウチなし）
 */
export const createApiSheet = (sheetNumber: number = 1): Sheet => {
  const layers = [createDefaultLayer()];
  return {
    id: uuidv4(),
    title: getAppCopy().default.sheetTitle(sheetNumber),
    pouches: [],
    groups: [],
    layers,
    activeLayerId: layers[0].id,
    createdAt: new Date(),
    lastPouchValue: 0,
    lastPouchColor: POUCH_COLORS[0].id,
    lastGroupColor: GROUP_COLORS[0].id,
    history: [],
    historyIndex: -1,
    snapshots: [],
  };
};

/**
 * Bridge API 用シート作成とストレージ保存
 */
export const createAndSaveApiSheet = async (sheetsCount: number): Promise<Sheet> => {
  const newSheet = createApiSheet(sheetsCount + 1);

  try {
    const { SheetStorage } = await import('./StorageUtils');
    await SheetStorage.saveSheet(newSheet);
    await SheetStorage.saveActiveSheetId(newSheet.id);
    return newSheet;
  } catch (error) {
    console.error('API 新規シートの保存でエラーが発生しました:', error);
    throw error;
  }
};

/**
 * デフォルトシートを作成する
 * @param sheetNumber シート番号（タイトル用）
 * @returns 初期パウチを含むデフォルトシート
 */
export const createDefaultSheet = (sheetNumber: number = 1): Sheet => {
  const sheetId = uuidv4();
  const defaultPouch = createDefaultPouch();
  const pouches = [defaultPouch];
  const groups: Group[] = [];
  
  // 初期履歴エントリを作成
  const initialHistoryEntry = createInitialHistoryEntry(pouches, groups);
  
  const layers = [createDefaultLayer()];
  return {
    id: sheetId,
    title: getAppCopy().default.sheetTitle(sheetNumber),
    pouches: pouches,
    groups: groups,
    layers,
    activeLayerId: layers[0].id,
    createdAt: new Date(),
    lastPouchValue: 0,
    lastPouchColor: POUCH_COLORS[0].id,
    lastGroupColor: GROUP_COLORS[0].id,
    history: [initialHistoryEntry],
    historyIndex: 0,
    snapshots: []
  };
};

/**
 * 初回起動時専用のウェルカムシートを作成する
 * @returns ウェルカムパウチとグループを含む初回起動用シート
 */
const createWelcomeSheet = (): Sheet => {
  const w = getAppCopy().welcome;
  const sheetId = uuidv4();
  const welcomePouches = createWelcomePouches();
  const welcomeGroups = createWelcomeGroups(welcomePouches);
  const welcomeLayer = createDefaultLayer(w.defaultLayerName);
  const welcomeLayers = [welcomeLayer];
  
  // 1. 初期状態（1個の初期パウチがある状態）の履歴エントリを作成
  const defaultPouch = createDefaultPouch();
  const initialHistoryEntry = createInitialHistoryEntry([defaultPouch], []);
  
  // 2. ウェルカムコンテンツの履歴エントリを作成
  const welcomeHistoryEntry: SheetHistoryEntry = {
    timestamp: new Date(),
    groups: welcomeGroups,
    pouches: welcomePouches,
    description: getHistoryStorageCopy().dynamic.welcomeContent,
    type: HistoryActionType.ADD_GROUP, // ウェルカムコンテンツの追加として記録
    targetId: undefined,
    layers: welcomeLayers.map(l => ({ ...l })),
    activeLayerId: welcomeLayer.id,
  };
  
  return {
    id: sheetId,
    title: w.sheetTitle,
    pouches: welcomePouches,
    groups: welcomeGroups,
    layers: welcomeLayers,
    activeLayerId: welcomeLayer.id,
    createdAt: new Date(),
    lastPouchValue: 800, // 最後のパウチの値に合わせる
    lastPouchColor: "orange",
    lastGroupColor: "blue",
    history: [initialHistoryEntry, welcomeHistoryEntry], // 0番目: 初期パウチ状態, 1番目: ウェルカム状態
    historyIndex: 1, // 現在はウェルカムコンテンツを表示
    snapshots: []
  };
};

/**
 * 初回起動用のデフォルトシート配列を作成する
 * @returns 初期シートを含む配列
 */
export const createDefaultSheets = (): Sheet[] => {
  return [createDefaultSheet(1)];
};

/**
 * 初回起動用のウェルカムシート配列を作成する
 * @returns ウェルカムシートを含む配列
 */
const createWelcomeSheets = (): Sheet[] => {
  return [createWelcomeSheet()];
};

/**
 * 初期状態用のシート配列を取得する
 * ストレージからシートを読み込み、存在しない場合はデフォルトシートを返す
 * @returns Promise<Sheet[]> - シート配列
 */
export const getInitialSheets = async (): Promise<Sheet[]> => {
  try {
    // 動的インポートでStorageUtilsを読み込み（循環依存を避けるため）
    const { SheetStorage } = await import('./StorageUtils');
    
    // ストレージからシートを取得
    const storedSheets = await SheetStorage.getAllSheets();
    
    if (storedSheets.length > 0) {
      return storedSheets;
    } else {
      const welcomeSheets = createWelcomeSheets();
      
      // ウェルカムシートをストレージに保存
      for (const sheet of welcomeSheets) {
        await SheetStorage.saveSheet(sheet);
      }
      
      // 最初のシートをアクティブに設定
      if (welcomeSheets.length > 0) {
        await SheetStorage.saveActiveSheetId(welcomeSheets[0].id);
      }
      
      return welcomeSheets;
    }
  } catch (error) {
    console.error('初期シート取得でエラーが発生しました:', error);
    // エラー時はメモリ上のウェルカムシートを返す
    return createWelcomeSheets();
  }
};

/**
 * 新規シート作成用の完全なシートを作成する
 * @param sheetsCount 現在のシート数
 * @returns 初期パウチを含む新規シート
 */
export const createNewSheetWithPouch = (sheetsCount: number): Sheet => {
  return createDefaultSheet(sheetsCount + 1);
};

/**
 * 唯一のシート削除時の初期化用シートを作成する
 * @returns 初期パウチを含む初期化シート
 */
export const createInitializationSheet = (): Sheet => {
  return createDefaultSheet(1);
};

/**
 * シート作成とストレージ保存を統合した関数
 * @param sheetsCount 現在のシート数
 * @returns Promise<Sheet> - 作成・保存されたシート
 */
export const createAndSaveNewSheet = async (sheetsCount: number): Promise<Sheet> => {
  const newSheet = createNewSheetWithPouch(sheetsCount);
  
  try {
    // 動的インポートでStorageUtilsを読み込み
    const { SheetStorage } = await import('./StorageUtils');
    
    // シートをストレージに保存
    await SheetStorage.saveSheet(newSheet);
    await SheetStorage.saveActiveSheetId(newSheet.id);
    
    return newSheet;
  } catch (error) {
    console.error('新規シートの保存でエラーが発生しました:', error);
    throw error;
  }
};

/**
 * 初期化シートの作成とストレージ保存を統合した関数
 * @returns Promise<Sheet> - 作成・保存された初期化シート
 */
export const createAndSaveInitializationSheet = async (): Promise<Sheet> => {
  const initSheet = createInitializationSheet();
  
  try {
    // 動的インポートでStorageUtilsを読み込み
    const { SheetStorage } = await import('./StorageUtils');
    
    // シートをストレージに保存
    await SheetStorage.saveSheet(initSheet);
    await SheetStorage.saveActiveSheetId(initSheet.id);
    
    return initSheet;
  } catch (error) {
    console.error('初期化シートの保存でエラーが発生しました:', error);
    throw error;
  }
};
